import React from 'react';
import { TCN } from '../../tcn/ui-kit/tokens';
import { formatDiamonds, formatUSD, ENCRYPTED_PLACEHOLDER } from './format';

/**
 * DiamondAmount — renders a diamond count in compact form, with optional USD
 * conversion, suitable for both clear-text and encrypted-pending values.
 *
 * Props:
 *   - value: number | null | undefined — diamonds count (already decrypted).
 *   - encrypted: boolean — when true, ignores `value` and renders the
 *     ENCRYPTED_PLACEHOLDER ("—"). The component caller passes encrypted=true
 *     when only the *_enc column is available client-side.
 *   - usdRate: number | undefined — when provided, also shows USD equivalent.
 *   - mono: boolean (default true) — tabular-nums.
 *   - color: optional CSS color override.
 */
const DiamondAmount = ({
  value,
  encrypted = false,
  usdRate,
  mono = true,
  color,
  className = '',
  style,
  showIcon = false,
}) => {
  const displayValue = encrypted ? ENCRYPTED_PLACEHOLDER : formatDiamonds(value);
  const usd =
    !encrypted && typeof value === 'number' && typeof usdRate === 'number'
      ? formatUSD(value * usdRate)
      : null;

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 6,
        fontFamily: mono ? TCN.fontMono : TCN.fontSans,
        fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
        color: color ?? TCN.text,
        ...style,
      }}
    >
      {showIcon && (
        <span aria-hidden="true" style={{ color: TCN.diamond }}>
          ◆
        </span>
      )}
      <span>{displayValue}</span>
      {usd && (
        <span style={{ fontSize: '0.75em', color: TCN.textDim }}>{usd}</span>
      )}
    </span>
  );
};

export default DiamondAmount;
